import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, ArrowRight, Cloud, CloudRain, Sun, Moon, ChefHat,
  Coffee, MapPin, Heart, Compass, Utensils, BrainCircuit, Mail,
  Check, Network,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------- Mock signal data for Auto-Sense (NO real API calls — design spec) ----------
const AUTO_SENSE_FRAMES = [
  {
    time: { label: "8:14 PM", icon: Moon, sub: "Wednesday evening" },
    weather: { label: "Raining · 21°C", icon: CloudRain, sub: "Drizzle for 2hrs" },
    mood: { label: "Cozy", icon: Heart, sub: "Recovery mode" },
    meal: { label: "Dinner", icon: Utensils, sub: "Comfort cravings rising" },
    pick: "Hyderabadi Dum Biryani",
    pickWhy: "Native to your Telangana roots · Perfect rainy-night comfort",
    confidence: 92,
  },
  {
    time: { label: "1:32 PM", icon: Sun, sub: "Sunny Tuesday" },
    weather: { label: "Hot · 33°C", icon: Sun, sub: "Light & refreshing favored" },
    mood: { label: "Energetic", icon: Sparkles, sub: "Exploration day" },
    meal: { label: "Lunch", icon: Utensils, sub: "Quick & vibrant" },
    pick: "Goan Prawn Curry Rice",
    pickWhy: "Coastal lightness · Bright spice tier 3/5",
    confidence: 87,
  },
  {
    time: { label: "9:02 AM", icon: Coffee, sub: "Slow Sunday morning" },
    weather: { label: "Cool & cloudy", icon: Cloud, sub: "Overcast, mild" },
    mood: { label: "Relaxed", icon: Heart, sub: "Weekend rhythm" },
    meal: { label: "Breakfast", icon: Utensils, sub: "Indulgent & familiar" },
    pick: "Mysore Masala Dosa",
    pickWhy: "From your favorite cuisine · Comforting Karnataka classic",
    confidence: 95,
  },
];

// ---------- 10 engines (Hub-Spoke) ----------
const ENGINES = [
  { name: "Identity DNA", icon: BrainCircuit },
  { name: "Geo DNA", icon: MapPin },
  { name: "Taste DNA", icon: Heart },
  { name: "Context Intelligence", icon: Compass },
  { name: "Relationship", icon: Network },
  { name: "Kitchen Intelligence", icon: ChefHat },
  { name: "Food Universe", icon: Utensils },
  { name: "Food Memory Graph", icon: BrainCircuit },
  { name: "Recommendation", icon: Sparkles },
  { name: "Analytics", icon: Compass },
];

// ---------- Editorial food images (from design spec + curated) ----------
const FOOD_IMAGES = [
  "https://images.unsplash.com/photo-1631452180519-c014fe946bc7?auto=format&fit=crop&w=900&q=80", // biryani
  "https://images.unsplash.com/photo-1565557623262-b51c2513a641?auto=format&fit=crop&w=900&q=80", // dosa
  "https://images.unsplash.com/photo-1606491956689-2ea866880c84?auto=format&fit=crop&w=900&q=80", // pasta
  "https://images.unsplash.com/photo-1601050690597-df0568f70950?auto=format&fit=crop&w=900&q=80", // ramen
  "https://images.unsplash.com/photo-1567188040759-fb8a883dc6d8?auto=format&fit=crop&w=900&q=80", // burger
  "https://images.unsplash.com/photo-1567337710282-00832b415979?auto=format&fit=crop&w=900&q=80", // sushi
];

// ============================================================
//  AUTO-SENSE PREVIEW CARD
// ============================================================
function AutoSenseTile({ k, item }) {
  const Icon = item.icon;
  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl px-4 py-3 border border-stone-200/70 shadow-sm" data-testid={`auto-tile-${k}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-stone-500 font-medium">
        <Icon className="w-3 h-3" strokeWidth={1.75} /> {k}
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={item.label}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="text-base font-medium text-stone-900 mt-1 leading-tight"
        >
          {item.label}
        </motion.div>
      </AnimatePresence>
      <div className="text-[11px] text-stone-500 mt-0.5">{item.sub}</div>
    </div>
  );
}

function AutoSenseMockup() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % AUTO_SENSE_FRAMES.length), 4200);
    return () => clearInterval(t);
  }, []);
  const f = AUTO_SENSE_FRAMES[idx];
  return (
    <div className="relative">
      {/* warm aura */}
      <div className="absolute -inset-8 bg-[radial-gradient(circle_at_center,_rgba(227,93,43,0.15),transparent_70%)] blur-2xl" />
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="relative bg-[#FDFBF7] rounded-[2rem] border border-stone-200 shadow-[0_30px_80px_-30px_rgba(42,36,33,0.25)] p-6 md:p-7 w-full max-w-[440px]"
        data-testid="auto-sense-mockup"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#E35D2B]/15 flex items-center justify-center">
              <Compass className="w-3.5 h-3.5 text-[#E35D2B]" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#E35D2B] font-semibold">Mother Drive</div>
              <div className="text-sm font-medium text-stone-900">What YUMO sees right now</div>
            </div>
          </div>
          <div className="text-[11px] text-stone-500">
            Conf <span className="text-stone-900 font-semibold tabular-nums">{f.confidence}%</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <AutoSenseTile k="Time" item={f.time} />
          <AutoSenseTile k="Weather" item={f.weather} />
          <AutoSenseTile k="Mood read" item={f.mood} />
          <AutoSenseTile k="Meal" item={f.meal} />
        </div>

        <div className="mt-5 pt-5 border-t border-stone-200/70">
          <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-medium">YUMO recommends</div>
          <AnimatePresence mode="wait">
            <motion.div
              key={f.pick}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="font-serif text-xl text-stone-900 mt-1 leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {f.pick}
              </div>
              <div className="text-xs text-stone-500 mt-1">{f.pickWhy}</div>
            </motion.div>
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}

// ============================================================
//  HUB-SPOKE DIAGRAM (10 engines orbiting Mother Drive)
// ============================================================
function HubSpoke() {
  // 10 engines split into outer (6) and inner (4) rings for visual rhythm
  const outer = ENGINES.slice(0, 6);
  const inner = ENGINES.slice(6);
  return (
    <div className="relative aspect-square w-full max-w-[560px] mx-auto" data-testid="hub-spoke">
      {/* outer ring */}
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0"
      >
        {outer.map((e, i) => {
          const angle = (i / outer.length) * Math.PI * 2 - Math.PI / 2;
          const r = 45;
          const x = 50 + Math.cos(angle) * r;
          const y = 50 + Math.sin(angle) * r;
          const Icon = e.icon;
          return (
            <motion.div
              key={e.name}
              animate={{ rotate: -360 }}
              transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <div className="bg-white border border-stone-200 rounded-2xl px-3 py-2 shadow-sm flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5 text-[#E35D2B]" strokeWidth={1.75} />
                <span className="text-[11px] font-medium text-stone-700 whitespace-nowrap">{e.name}</span>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* inner ring */}
      <motion.div
        animate={{ rotate: -360 }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
        className="absolute inset-0"
      >
        {inner.map((e, i) => {
          const angle = (i / inner.length) * Math.PI * 2 + Math.PI / 4;
          const r = 24;
          const x = 50 + Math.cos(angle) * r;
          const y = 50 + Math.sin(angle) * r;
          const Icon = e.icon;
          return (
            <motion.div
              key={e.name}
              animate={{ rotate: 360 }}
              transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              <div className="bg-[#F7F4EB] border border-stone-200 rounded-xl px-2.5 py-1.5 flex items-center gap-1.5">
                <Icon className="w-3 h-3 text-[#E35D2B]" strokeWidth={1.75} />
                <span className="text-[10px] font-medium text-stone-700 whitespace-nowrap">{e.name}</span>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      {/* center: Mother Drive */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          animate={{ scale: [1, 1.04, 1] }}
          transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
          className="relative"
        >
          <div className="absolute -inset-6 bg-[radial-gradient(circle_at_center,_rgba(227,93,43,0.25),transparent_70%)] blur-xl" />
          <div className="relative w-28 h-28 md:w-32 md:h-32 rounded-full bg-[#E35D2B] flex items-center justify-center shadow-lg">
            <div className="text-center text-[#FDFBF7]">
              <div className="text-[9px] uppercase tracking-[0.25em] opacity-80">The Hub</div>
              <div className="font-serif text-xl md:text-2xl font-semibold leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Mother<br />Drive
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ============================================================
//  EDITORIAL FOOD MARQUEE
// ============================================================
function FoodMarquee() {
  const items = [...FOOD_IMAGES, ...FOOD_IMAGES];
  return (
    <div className="overflow-hidden relative">
      <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-[#FDFBF7] to-transparent z-10 pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-[#FDFBF7] to-transparent z-10 pointer-events-none" />
      <motion.div
        className="flex gap-6 md:gap-10"
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
      >
        {items.map((src, i) => (
          <div
            key={i}
            className="shrink-0 w-[260px] h-[340px] md:w-[320px] md:h-[420px] rounded-3xl overflow-hidden bg-stone-200"
          >
            <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
          </div>
        ))}
      </motion.div>
    </div>
  );
}

// ============================================================
//  WAITLIST FORM — Early Access Program (5 fields)
// ============================================================
const MOBILE_RE = /^[\d\s()+\-]{7,20}$/;
const CUISINES = ["Indian", "Italian", "Chinese", "Mexican", "Japanese", "Thai", "Mediterranean", "American", "French", "Korean", "Other"];

function WaitlistField({ name, label, type = "text", required, placeholder, value, onChange, error, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-semibold flex items-center gap-1">
        {label}{required && <span className="text-[#E35D2B]">*</span>}
      </span>
      {children || (
        <input
          type={type}
          required={required}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`w-full mt-1.5 bg-[#FDFBF7] text-stone-900 placeholder:text-stone-400 rounded-2xl px-4 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-[#E35D2B]/40 border ${error ? "border-red-500" : "border-transparent"}`}
          data-testid={`waitlist-field-${name}`}
        />
      )}
      {error && <span className="text-red-600 text-xs mt-1 block" data-testid={`waitlist-error-${name}`}>{error}</span>}
    </label>
  );
}

function WaitlistForm() {
  const [form, setForm] = useState({ full_name: "", email: "", mobile: "", city: "", favorite_cuisine: "" });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState({ status: "idle", position: null, total: null, alreadyJoined: false, error: null });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = useCallback(async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.full_name.trim()) errs.full_name = "Required";
    if (!form.email.trim()) errs.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = "Invalid email";
    if (form.mobile.trim() && !MOBILE_RE.test(form.mobile.trim())) errs.mobile = "Invalid number";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setState((s) => ({ ...s, status: "loading" }));
    try {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        mobile: form.mobile.trim() || null,
        city: form.city.trim() || null,
        favorite_cuisine: form.favorite_cuisine.trim() || null,
        source: "launch",
      };
      const { data } = await axios.post(`${API}/waitlist/join`, payload);
      setState({ status: "success", position: data.position, total: data.total, alreadyJoined: data.already_joined });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Couldn&apos;t add you — try again in a moment.";
      setState({ status: "error", error: msg });
    }
  }, [form]);

  if (state.status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="bg-[#FDFBF7] rounded-3xl p-8 md:p-12 text-center max-w-xl mx-auto"
        data-testid="waitlist-success"
      >
        <div className="w-14 h-14 mx-auto rounded-full bg-[#E35D2B]/15 flex items-center justify-center mb-5">
          <Check className="w-6 h-6 text-[#E35D2B]" strokeWidth={2} />
        </div>
        <div className="font-serif text-3xl md:text-4xl text-stone-900 leading-tight mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Welcome to <em>YUMO</em>.
        </div>
        <div className="text-stone-600 text-base md:text-lg leading-relaxed mb-4">
          You&apos;re now part of the future of food intelligence.
        </div>
        {!state.alreadyJoined && state.position != null && (
          <div className="inline-block bg-[#F7F4EB] border border-stone-200 rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.18em] text-stone-600 font-medium">
            Your spot · #{state.position}
          </div>
        )}
        {state.alreadyJoined && (
          <div className="text-stone-500 text-sm">You&apos;re already on the list — we&apos;ll be in touch soon.</div>
        )}
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-[#FDFBF7] rounded-3xl p-6 md:p-10 max-w-2xl mx-auto text-left" data-testid="waitlist-form">
      <div className="space-y-4">
        <WaitlistField name="full_name" label="Full Name" required placeholder="Priya Sharma"
          value={form.full_name} onChange={update("full_name")} error={errors.full_name} />
        <WaitlistField name="email" label="Email Address" type="email" required placeholder="you@example.com"
          value={form.email} onChange={update("email")} error={errors.email} />
        <div className="grid sm:grid-cols-2 gap-4">
          <WaitlistField name="mobile" label="Mobile Number" type="tel" placeholder="+91 98765 43210"
            value={form.mobile} onChange={update("mobile")} error={errors.mobile} />
          <WaitlistField name="city" label="City" placeholder="Bangalore"
            value={form.city} onChange={update("city")} />
        </div>
        <WaitlistField name="favorite_cuisine" label="Favorite Cuisine">
          <select
            value={form.favorite_cuisine}
            onChange={update("favorite_cuisine")}
            className="w-full mt-1.5 bg-[#FDFBF7] text-stone-900 rounded-2xl px-4 py-3.5 text-base focus:outline-none focus:ring-2 focus:ring-[#E35D2B]/40 border border-transparent appearance-none"
            data-testid="waitlist-field-favorite_cuisine"
          >
            <option value="">Pick one (optional)</option>
            {CUISINES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </WaitlistField>
      </div>
      <button
        type="submit"
        disabled={state.status === "loading"}
        className="w-full mt-7 rounded-full px-7 py-4 bg-stone-900 text-[#FDFBF7] text-base font-semibold tracking-wide hover:bg-stone-800 transition disabled:opacity-60 flex items-center justify-center gap-2"
        data-testid="waitlist-submit-button"
      >
        {state.status === "loading" ? "Saving…" : <>Join Waitlist <ArrowRight className="w-4 h-4" strokeWidth={2} /></>}
      </button>
      {state.status === "error" && (
        <div className="text-red-600 text-sm mt-3 text-center" data-testid="waitlist-error">{state.error}</div>
      )}
      <div className="text-[11px] text-stone-500 mt-4 text-center">
        We respect your inbox. No spam, ever. Unsubscribe anytime.
      </div>
    </form>
  );
}

// ============================================================
//  PAGE
// ============================================================
export default function Launch() {
  const [waitlistTotal, setWaitlistTotal] = useState(null);
  useEffect(() => {
    axios.get(`${API}/waitlist/stats`).then(({ data }) => setWaitlistTotal(data.total)).catch(() => {});
  }, []);

  const waitlistRef = useRef(null);
  const scrollToWaitlist = useCallback(() => {
    waitlistRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  return (
    <div className="bg-[#FDFBF7] text-stone-900 min-h-screen overflow-x-hidden" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }} data-testid="launch-page">
      {/* ---------- NAV ---------- */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FDFBF7]/70 border-b border-stone-200/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5" data-testid="launch-logo">
            <img
              src="/brand/yumo-wordmark.png"
              alt="YUMO"
              className="h-9 md:h-10 w-auto"
              draggable="false"
            />
            <span className="hidden md:inline-block text-[10px] uppercase tracking-[0.2em] text-stone-500 ml-2 border-l border-stone-300 pl-3">V11 · Early Access</span>
          </div>
          <nav className="flex items-center gap-5 md:gap-7 text-sm">
            <Link to="/" className="text-stone-700 hover:text-stone-900 transition hidden sm:inline" data-testid="launch-nav-main">Main app</Link>
            <Link to="/contact" className="text-stone-700 hover:text-stone-900 transition hidden sm:inline">Contact</Link>
            <button
              onClick={scrollToWaitlist}
              className="bg-[#E35D2B] text-[#FDFBF7] rounded-full px-5 py-2 text-sm font-semibold hover:bg-[#C94A1F] transition"
              data-testid="early-access-nav-cta"
            >
              Get early access
            </button>
          </nav>
        </div>
      </header>

      {/* ---------- HERO ---------- */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-28 pb-20 md:pb-32">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#E35D2B] font-semibold mb-6"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#E35D2B] animate-pulse" />
              V11 · Early Access Now Open
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] text-stone-900"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="launch-hero-headline"
            >
              The less YUMO asks,<br />
              the <em className="text-[#E35D2B]">smarter</em> it becomes.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="text-lg md:text-xl text-stone-600 mt-6 max-w-xl leading-relaxed"
            >
              Your personal food intelligence buddy. No more &quot;what should I eat?&quot; — YUMO senses your day,
              weather and mood, then picks the right dish at the right moment.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-wrap items-center gap-4 mt-10"
            >
              <button
                onClick={scrollToWaitlist}
                className="bg-[#E35D2B] text-[#FDFBF7] rounded-full px-7 py-3.5 text-base font-semibold tracking-wide hover:bg-[#C94A1F] transition shadow-sm flex items-center gap-2"
                data-testid="hero-cta-primary"
              >
                Join early access <ArrowRight className="w-4 h-4" strokeWidth={2} />
              </button>
              <a
                href="#how-it-works"
                className="text-stone-800 text-base hover:text-[#E35D2B] transition inline-flex items-center gap-1.5"
                data-testid="hero-cta-secondary"
              >
                See how it works <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
            </motion.div>
            {waitlistTotal != null && (
              <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="text-sm text-stone-500 mt-8 flex items-center gap-2"
                data-testid="hero-social-proof"
              >
                <span className="flex -space-x-1.5">
                  {[0,1,2,3].map(i => (
                    <span key={i} className="w-6 h-6 rounded-full border-2 border-[#FDFBF7]" style={{ background: ["#E35D2B","#C94A1F","#8B5A2B","#A66F3D"][i] }} />
                  ))}
                </span>
                <span><span className="font-semibold text-stone-700 tabular-nums">{waitlistTotal}</span> food lovers already in line</span>
              </motion.div>
            )}
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 flex justify-center lg:justify-end"
          >
            <AutoSenseMockup />
          </motion.div>
        </div>
      </section>

      {/* ---------- HUB-SPOKE STORY ---------- */}
      <section id="how-it-works" className="bg-[#F7F4EB] border-y border-stone-200/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#E35D2B] font-semibold mb-4">The architecture</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Ten engines.<br />
                One <em>Mother Drive</em>.
              </h2>
              <p className="text-stone-600 text-lg leading-relaxed mt-6 max-w-md">
                YUMO doesn&apos;t guess. It computes your culinary DNA across ten neural engines —
                Identity, Geo, Taste, Context, Relationship, Kitchen, Memory and more — orchestrated
                in real-time by the Mother Drive.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Auto-senses weather, time, and mood — no surveys",
                  "Learns from every like, dislike and repeat",
                  "Honors your roots, region, and rhythm",
                ].map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-stone-700 text-sm">
                    <Check className="w-4 h-4 text-[#E35D2B] mt-0.5 shrink-0" strokeWidth={2.5} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7">
              <HubSpoke />
            </div>
          </div>
        </div>
      </section>

      {/* ---------- EDITORIAL FOOD STRIP ---------- */}
      <section className="py-20 md:py-28 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#E35D2B] font-semibold mb-3">Editorial</div>
          <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight text-stone-900 max-w-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Food is memory.<br />
            <em className="text-stone-500">We treat it that way.</em>
          </h2>
        </div>
        <FoodMarquee />
      </section>

      {/* ---------- DIFFERENTIATORS BENTO ---------- */}
      <section className="bg-[#F7F4EB] border-y border-stone-200/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#E35D2B] font-semibold mb-4">Why YUMO is different</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-stone-900 max-w-3xl mb-14" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Not another recipe app. Not a delivery menu.
          </h2>
          <div className="grid md:grid-cols-6 gap-6 md:gap-8">
            {/* Large card */}
            <div className="md:col-span-4 bg-[#FDFBF7] rounded-3xl p-8 md:p-12 border border-stone-200/70 shadow-sm" data-testid="bento-card-1">
              <Compass className="w-7 h-7 text-[#E35D2B] mb-6" strokeWidth={1.5} />
              <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Zero decision<br/>fatigue.
              </h3>
              <p className="text-stone-600 text-base mt-4 max-w-md">
                Auto-Sense reads your weather, time, behaviour and mood. You just open the app.
              </p>
            </div>
            {/* Tall card */}
            <div className="md:col-span-2 bg-[#FDFBF7] rounded-3xl p-8 md:p-10 border border-stone-200/70 shadow-sm" data-testid="bento-card-2">
              <BrainCircuit className="w-7 h-7 text-[#E35D2B] mb-6" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Never asks twice.
              </h3>
              <p className="text-stone-600 text-base mt-4">
                The Food Memory Graph remembers — your loves, your no-thank-yous, your comfort dishes.
              </p>
            </div>
            {/* Wide card */}
            <div className="md:col-span-3 bg-[#FDFBF7] rounded-3xl p-8 md:p-10 border border-stone-200/70 shadow-sm" data-testid="bento-card-3">
              <ChefHat className="w-7 h-7 text-[#E35D2B] mb-6" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Kitchen aware.
              </h3>
              <p className="text-stone-600 text-base mt-4">
                Tell it what&apos;s in your pantry. It&apos;ll only suggest what you can actually cook tonight.
              </p>
            </div>
            <div className="md:col-span-3 bg-[#FDFBF7] rounded-3xl p-8 md:p-10 border border-stone-200/70 shadow-sm" data-testid="bento-card-4">
              <MapPin className="w-7 h-7 text-[#E35D2B] mb-6" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight leading-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Honors your roots.
              </h3>
              <p className="text-stone-600 text-base mt-4">
                Geo DNA tunes recommendations to your state, city and travel context — the way your grandmother would.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- WAITLIST CTA ---------- */}
      <section ref={waitlistRef} className="bg-[#E35D2B] text-[#FDFBF7]" data-testid="waitlist-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="text-center mb-12">
            <div className="text-[10px] uppercase tracking-[0.25em] opacity-80 font-semibold mb-5">Early access</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-5" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Join the YUMO<br/><em>Early Access Program.</em>
            </h2>
            <p className="text-[#FDFBF7]/85 text-base md:text-lg max-w-xl mx-auto leading-relaxed">
              We&apos;re opening V11 to a small group of early believers. Tell us a little about yourself —
              we&apos;ll send your invite shortly.
            </p>
          </div>
          <WaitlistForm />
          {waitlistTotal != null && (
            <div className="text-[#FDFBF7]/70 text-sm mt-8 flex items-center justify-center gap-2" data-testid="waitlist-social-proof">
              <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span><span className="font-semibold text-[#FDFBF7] tabular-nums">{waitlistTotal}</span> food lovers already in line</span>
            </div>
          )}
        </div>
      </section>

      {/* ---------- FOOTER ---------- */}
      <footer className="bg-[#FDFBF7] py-16 border-t border-stone-200/60">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <img
            src="/brand/yumo-logo.png"
            alt="YUMO — Yummy Food For Your Mood"
            className="h-20 md:h-24 w-auto mx-auto mb-6"
            draggable="false"
            data-testid="launch-footer-logo"
          />
          <div className="text-sm text-stone-500 flex items-center justify-center gap-5 mb-6">
            <Link to="/" className="hover:text-stone-900 transition" data-testid="footer-main">Main app</Link>
            <Link to="/privacy" className="hover:text-stone-900 transition">Privacy</Link>
            <Link to="/terms" className="hover:text-stone-900 transition">Terms</Link>
            <Link to="/contact" className="hover:text-stone-900 transition">Contact</Link>
          </div>
          <div className="text-xs text-stone-400">© 2026 YUMO · Yummy Food For Your Mood</div>
        </div>
      </footer>
    </div>
  );
}
